import { ClassyURLParams } from '../params/params.types'
import CrawlerElement from './crawler.element'
import { CurrencyOption, OrgTag } from '../config/config.types'
import { getCurrencyDecimalPlaces } from '../util/currency'
import { extractClassyParamsFromHref } from '../params/params.utils'
import {
  donateCTAClickedEvent,
  hostedDonationGridViewedEvent,
  studioGridAmountSelectionEvent,
  studioGridCurrencySelectionEvent,
  studioGridFrequencyToggleEvent,
} from '../host-page-events/host-page-event.definitions'
import { getBaseUri } from '../util/script-params'

export type CrawlerElementStrategy = 'donationForm' | 'classyHref' | 'default'

type StrategyHandler = (element: CrawlerElement) => void

interface DonationAmounts {
  name: string
  amount: number
  isVisible: boolean
  isDefault: boolean
}
interface Exchange {
  from: string
  from_generated_at: string
  to: string
  to_generated_at: string
  date: string
  rate: number
}

interface DonationFrequency {
  name: string
  amount: number
  isVisible: boolean
  isDefault: boolean
}

interface AmountSelectedDetail {
  from_amount?: number | string
  to_amount?: number | string
  preset_name?: string
  frequency?: string
  currency?: string
}

interface CurrencySelectedDetail {
  from_currency?: string
  to_currency: string
}

const CUSTOM_AMOUNT_PRESET_NAME = 'other'
const CUSTOM_AMOUNT_SELECTION_VALUE = 'Custom'

const RECURRING_CODE_TO_FREQUENCY: Record<string, string> = {
  '0': 'one-time',
  '1': 'yearly',
  '2': 'semi-annually',
  '4': 'quarterly',
  '12': 'monthly',
  '26': 'bi-weekly',
  '52': 'weekly',
  '365': 'daily',
}

const toNumericAmount = (amount: number | string | undefined): number | null => {
  if (typeof amount === 'undefined') {
    return null
  }

  const numericAmount = typeof amount === 'string' ? Number(amount) : amount

  return Number.isNaN(numericAmount) ? null : numericAmount
}

const formatAmountSelectionValue = (amount: number, currency: string): string => {
  return new Intl.NumberFormat('en-US', {
    currency,
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
    style: 'currency',
  }).format(Math.round(amount))
}

const handleDonationForm: StrategyHandler = async (element) => {
  try {
    await import('@classy/embedded-components/dist/ClassyDonationForm.js')

    const updateSelectedCurrency = (currencies: CurrencyOption[], selectedCurrency: string) => {
      return currencies.map((currency) => ({
        ...currency,
        selected: currency.value === selectedCurrency,
      }))
    }
    const getExchangeRate = async (fromCurrency: string, toCurrency: string): Promise<Exchange> => {
      // Endpoints are served from the monolith origin (e.g. https://giving.gofundme.com).
      const apiBaseUri = new URL(getBaseUri()).origin
      const response = await fetch(
        `${apiBaseUri}/api/checkout/currency?fromCurrency=${fromCurrency}&toCurrency=${toCurrency}`
      )
      if (!response) {
        throw new Error('Failed to fetch config')
      }
      return await response.json()
    }

    const donationForm = document.createElement('cl-donation-form')
    donationForm.setAttribute('campaign-id', element.campaignId)

    const isHostPageEventSyncSuppressed = (): boolean => {
      return Boolean(
        (donationForm as { __egHostPageEventSyncDepth?: number }).__egHostPageEventSyncDepth
      )
    }

    // Store original amounts in the base currency for accurate conversions
    let originalDonationAmounts: DonationAmounts[] = []
    let originalRecurringAmounts: DonationAmounts[] = []
    let originalMinimumDonationAmount: number | undefined
    let baseCurrency = ''

    // Track current selection and custom "Other" amounts so we can convert them on currency change
    const selectedPresetNameByFrequency: Record<string, string> = {}
    const customAmountByFrequencyAndCurrency: Record<string, Record<string, number>> = {}
    let currentFrequency: string | null = null
    let currentCurrency: string | null = null
    let lastTrackedAmountSelectionKey: string | null = null

    if (element.config) {
      // if disable-multi-currency exists in the org tags, only pass the default currency from the list
      const { options, default: defaultValue } = element.config.props.currencies
      let hasCurrencyTag = false
      if (element.config.props.orgTags) {
        hasCurrencyTag = element.config.props.orgTags.some(
          (t: OrgTag) => t.name === 'disable-multi-currency'
        )
      }

      const currenciesOptions: CurrencyOption[] = hasCurrencyTag
        ? options.filter((c: CurrencyOption) => c.value === defaultValue)
        : options

      // Store the base currency BEFORE updating the currencies object
      baseCurrency = defaultValue
      currentCurrency = defaultValue
      originalDonationAmounts = JSON.parse(JSON.stringify(element.config.props.donationAmounts))
      originalRecurringAmounts = JSON.parse(JSON.stringify(element.config.props.recurringAmounts))
      originalMinimumDonationAmount = element.config.props.minimumDonationAmount

      // Track initial selected frequency (fallback to 'one-time' when not configured)
      const frequencies: DonationFrequency[] = element.config.props.donationFrequencies || []
      const defaultFrequency = frequencies.find((f) => f.isDefault) || frequencies[0]
      if (defaultFrequency?.name) {
        currentFrequency = defaultFrequency.name
      } else {
        currentFrequency = 'one-time'
      }

      element.config.props.currencies = updateSelectedCurrency(currenciesOptions, defaultValue)
    }

    const updateAmountsByCurrencyExchange = (
      arr: DonationAmounts[],
      rate: number,
      selectedCurrency: string
    ) => {
      return arr.map((item) => ({
        ...item,
        amount: (item.amount * rate).toFixed(getCurrencyDecimalPlaces(selectedCurrency)),
      }))
    }

    // Helper: Resolve frequency and currency with fallbacks
    const resolveFrequencyAndCurrency = (
      eventFrequency: string | undefined,
      eventCurrency: string | undefined
    ): { frequency: string; currency: string } => {
      return {
        frequency: eventFrequency || currentFrequency || 'one-time',
        currency:
          eventCurrency ||
          currentCurrency ||
          (element.config?.props.currencies?.default as string) ||
          'USD',
      }
    }

    // Helper: Update preset amounts for a new currency
    const updatePresetAmountsForCurrency = async (targetCurrency: string): Promise<void> => {
      if (!element.config) {
        return
      }

      const baseFromCurrency = baseCurrency || 'USD'
      const exchangeRate = await getExchangeRate(baseFromCurrency, targetCurrency)

      element.config.props.donationAmounts = updateAmountsByCurrencyExchange(
        originalDonationAmounts,
        exchangeRate.rate,
        targetCurrency
      )
      element.config.props.recurringAmounts = updateAmountsByCurrencyExchange(
        originalRecurringAmounts,
        exchangeRate.rate,
        targetCurrency
      )
      element.config.props.currencies = updateSelectedCurrency(
        element.config.props.currencies,
        targetCurrency
      )

      if (typeof originalMinimumDonationAmount === 'number') {
        const decimals = getCurrencyDecimalPlaces(targetCurrency)
        const convertedMinimum = Number(
          (originalMinimumDonationAmount * exchangeRate.rate).toFixed(decimals)
        )
        element.config.props.minimumDonationAmount = convertedMinimum
      }
    }

    // Helper: Convert custom "Other" amounts and apply to form
    const updateCustomOtherAmountsForCurrency = async (
      previousCurrency: string,
      targetCurrency: string
    ): Promise<void> => {
      const externalCustomOverrides: Record<string, number | null> = {}

      const customExchange = await getExchangeRate(previousCurrency, targetCurrency)
      const customDecimals = getCurrencyDecimalPlaces(targetCurrency)

      Object.entries(selectedPresetNameByFrequency).forEach(([frequency, presetName]) => {
        if (presetName !== CUSTOM_AMOUNT_PRESET_NAME) {
          return
        }

        const perCurrencyCustom = customAmountByFrequencyAndCurrency[frequency]
        const previousCustom = perCurrencyCustom?.[previousCurrency]

        if (typeof previousCustom !== 'number') {
          return
        }

        const convertedCustom = Number(
          (previousCustom * customExchange.rate).toFixed(customDecimals)
        )

        if (!customAmountByFrequencyAndCurrency[frequency]) {
          customAmountByFrequencyAndCurrency[frequency] = {}
        }

        customAmountByFrequencyAndCurrency[frequency][targetCurrency] = convertedCustom
        externalCustomOverrides[frequency] = convertedCustom
      })

      if (Object.keys(externalCustomOverrides).length > 0) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const formWithCustomAmounts = donationForm as any
        formWithCustomAmounts.externalCustomAmounts = externalCustomOverrides
      }
    }

    // Apply URL query parameters to pre-select values on the inline grid
    // by mutating the config before the component renders.
    const urlParams = element.modalSDK.getParams()
    const resolvedFrequency =
      urlParams.frequency ||
      (urlParams.recurring && RECURRING_CODE_TO_FREQUENCY[urlParams.recurring]) ||
      urlParams.recurring
    const hasPresetOverrides =
      urlParams.preset1 || urlParams.preset2 || urlParams.preset3 || urlParams.preset4
    const hasUrlOverrides =
      urlParams.currency || resolvedFrequency || urlParams.amount || hasPresetOverrides

    if (element.config && hasUrlOverrides) {
      // 1. Preset overrides (preset1–preset4) are applied first, in the base
      //    currency, so that subsequent currency conversion uses the overridden
      //    values as the source of truth.
      if (hasPresetOverrides) {
        const applyPresets = (amounts: DonationAmounts[]) => {
          for (const key of ['preset1', 'preset2', 'preset3', 'preset4'] as const) {
            const raw = urlParams[key]
            if (!raw) {
              continue
            }
            const num = Number(raw)
            if (isNaN(num) || num <= 0) {
              continue
            }
            const entry = amounts.find((a) => a.name === key)
            if (entry) {
              entry.amount = num
            }
          }
        }
        applyPresets(element.config.props.donationAmounts as DonationAmounts[])
        applyPresets(originalDonationAmounts)
        applyPresets(element.config.props.recurringAmounts as DonationAmounts[])
        applyPresets(originalRecurringAmounts)
      }

      // 2. Currency conversion — converts all preset amounts via exchange rate.
      //    Must run after preset overrides so converted values reflect overrides.
      if (urlParams.currency && urlParams.currency !== currentCurrency) {
        await updatePresetAmountsForCurrency(urlParams.currency)
        currentCurrency = urlParams.currency
      }

      // 3. Frequency selection ('recurring' is a legacy alias for 'frequency')
      //    The toggle is binary: one-time vs recurring. If the resolved frequency
      //    is not visible, fall back to the first visible recurring option —
      //    matching the embedded-app's FrequencyToggleGroup behaviour.
      if (resolvedFrequency) {
        const frequencies = element.config.props.donationFrequencies as DonationFrequency[]
        const isVisibleFrequency = frequencies?.some(
          (f) => f.name === resolvedFrequency && f.isVisible
        )

        let targetFrequency: string | null = null

        if (isVisibleFrequency) {
          targetFrequency = resolvedFrequency
        } else if (resolvedFrequency !== 'one-time') {
          const firstVisibleRecurring = frequencies?.find(
            (f) => f.isVisible && f.name !== 'one-time'
          )
          if (firstVisibleRecurring) {
            targetFrequency = firstVisibleRecurring.name
          }
        }

        if (targetFrequency && frequencies) {
          frequencies.forEach((f) => {
            f.isDefault = f.name === targetFrequency
          })
          currentFrequency = targetFrequency
        }
      }

      // 4. Amount pre-selection — match against the active preset list
      if (urlParams.amount) {
        const numericAmount = Number(urlParams.amount)
        if (!isNaN(numericAmount) && numericAmount > 0) {
          const isOneTime = (currentFrequency || 'one-time') === 'one-time'
          const amounts = isOneTime
            ? (element.config.props.donationAmounts as DonationAmounts[])
            : (element.config.props.recurringAmounts as DonationAmounts[])
          if (amounts) {
            const matched = amounts.find(
              (a) => Math.round(Number(a.amount)) === Math.round(numericAmount)
            )
            if (matched) {
              amounts.forEach((a) => {
                a.isDefault = a === matched
              })
            }
          }
        }
      }
    }

    // @ts-expect-error | Custom element custom attribute
    donationForm.config = element.config.props

    element.el.replaceWith(donationForm)

    // Store the replacement so it can be restored on destroy
    element.setReplacement(donationForm)

    // If the URL amount doesn't match any preset, push it as a custom "Other" value
    if (element.config && urlParams.amount) {
      const numericAmount = Number(urlParams.amount)
      if (!isNaN(numericAmount) && numericAmount > 0) {
        const freq = currentFrequency || 'one-time'
        const isOneTime = freq === 'one-time'
        const amounts = isOneTime
          ? (element.config.props.donationAmounts as DonationAmounts[])
          : (element.config.props.recurringAmounts as DonationAmounts[])
        const hasPresetMatch = amounts?.some(
          (a) => Math.round(Number(a.amount)) === Math.round(numericAmount)
        )
        if (!hasPresetMatch) {
          const currency = currentCurrency || baseCurrency || 'USD'
          selectedPresetNameByFrequency[freq] = CUSTOM_AMOUNT_PRESET_NAME
          if (!customAmountByFrequencyAndCurrency[freq]) {
            customAmountByFrequencyAndCurrency[freq] = {}
          }
          customAmountByFrequencyAndCurrency[freq][currency] = numericAmount
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ;(donationForm as any).externalCustomAmounts = { [freq]: numericAmount }
        }
      }
    }

    // Track the hosted donation grid viewed event
    const props = element.config!.props
    const defaultFreq = currentFrequency || 'one-time'
    const amounts: DonationAmounts[] =
      defaultFreq === 'one-time'
        ? (props.donationAmounts as DonationAmounts[]) || []
        : (props.recurringAmounts as DonationAmounts[]) || []
    const donationAmountOptions = amounts
      .filter((a) => a.isVisible && a.name !== 'other')
      .map((a) => String(a.amount))
      .join(',')
    const donationFrequencyOptions = ((props.donationFrequencies as DonationFrequency[]) || [])
      .filter((f) => f.isVisible)
      .map((f) => f.name)
      .join(',')
    // props.currencies has already been replaced with CurrencyOption[] at this point
    const currenciesArray = Array.isArray(props.currencies)
      ? (props.currencies as CurrencyOption[])
      : []
    const donationCurrencyOptions = currenciesArray.map((c) => c.value).join(',')
    const defaultAmount = amounts.find((a) => a.isDefault)
    const donationAmountDefault = defaultAmount ? Number(defaultAmount.amount) : 0
    element.hostPageEventInstrumentation?.track(hostedDonationGridViewedEvent, {
      campaignId: element.campaignId,
      data: {
        donation_amount_default: donationAmountDefault,
        donation_amount_options: donationAmountOptions,
        donation_currency_default: baseCurrency || 'USD',
        donation_currency_options: donationCurrencyOptions,
        donation_frequency_default: defaultFreq,
        donation_frequency_options: donationFrequencyOptions,
      },
    })

    // Subscribe to donation form submission
    donationForm.addEventListener('classy-donation-submit', (event: CustomEvent) => {
      event.preventDefault()
      event.stopPropagation()

      const donationDetails: {
        frequency: string
        amount: string
        recurringEndDate: string
        selectedCurrency: string
      } = event.detail

      const ctaFrequency = donationDetails.frequency || currentFrequency || 'one-time'
      const ctaText = ctaFrequency === 'monthly' ? 'Donate monthly' : 'Donate one time'

      element.hostPageEventInstrumentation?.track(donateCTAClickedEvent, {
        campaignId: element.campaignId,
        data: {
          donation_amount: parseFloat(donationDetails.amount) || 0,
          donation_currency: donationDetails.selectedCurrency || currentCurrency || 'USD',
          donation_frequency: ctaFrequency,
          link_url: window.location.href,
          text: ctaText,
        },
      })

      const params: ClassyURLParams = {}

      if (donationDetails.frequency) {
        params.frequency = donationDetails.frequency
      }

      if (donationDetails.amount) {
        params.amount = donationDetails.amount
      }

      if (donationDetails.recurringEndDate) {
        params.recurringEndDate = donationDetails.recurringEndDate
      }

      if (donationDetails.selectedCurrency) {
        params.currency = donationDetails.selectedCurrency
      }

      element.modalSDK.open(element.campaignId, params)
    })

    // Subscribe to frequency changes
    donationForm.addEventListener('frequency_change', (event: CustomEvent) => {
      const shouldSuppressTracking = isHostPageEventSyncSuppressed()
      const { from_frequency, to_frequency } = event.detail

      if (to_frequency) {
        currentFrequency = to_frequency
      }

      if (
        shouldSuppressTracking ||
        !from_frequency ||
        !to_frequency ||
        from_frequency === to_frequency
      ) {
        return
      }

      element.hostPageEventInstrumentation?.track(studioGridFrequencyToggleEvent, {
        campaignId: element.campaignId,
        data: {
          from_frequency,
          to_frequency,
        },
      })
    })

    // Subscribe to amount selection changes
    donationForm.addEventListener('amount_selected', (event: CustomEvent<AmountSelectedDetail>) => {
      const shouldSuppressTracking = isHostPageEventSyncSuppressed()
      const {
        from_amount,
        to_amount,
        preset_name,
        frequency: eventFrequency,
        currency: eventCurrency,
      } = event.detail || {}

      const { frequency, currency } = resolveFrequencyAndCurrency(eventFrequency, eventCurrency)

      if (!frequency || to_amount == null) {
        return
      }

      let resolvedPresetName: string | undefined = preset_name

      if (!resolvedPresetName && element.config) {
        const numericToAmount = typeof to_amount === 'string' ? Number(to_amount) : to_amount
        const isOneTime = frequency === 'one-time'
        const amountsSource = isOneTime
          ? (element.config.props.donationAmounts as DonationAmounts[])
          : (element.config.props.recurringAmounts as DonationAmounts[])

        // The DonationForm component rounds preset amounts to whole numbers for display,
        // but the SDK stores converted amounts with decimals (e.g., '1076.40').
        // We need to round both sides when comparing to handle this mismatch.
        const roundedToAmount = Math.round(numericToAmount)
        const match = amountsSource?.find(
          (item) => Math.round(Number(item.amount)) === roundedToAmount
        )
        resolvedPresetName = match?.name
      }

      if (!resolvedPresetName) {
        resolvedPresetName = CUSTOM_AMOUNT_PRESET_NAME
      }

      selectedPresetNameByFrequency[frequency] = resolvedPresetName
      const numericToAmount = toNumericAmount(to_amount)
      const isCustomAmountSelection = resolvedPresetName === CUSTOM_AMOUNT_PRESET_NAME
      const hasKnownAmount = numericToAmount !== null

      if (isCustomAmountSelection) {
        if (hasKnownAmount) {
          if (!customAmountByFrequencyAndCurrency[frequency]) {
            customAmountByFrequencyAndCurrency[frequency] = {}
          }
          customAmountByFrequencyAndCurrency[frequency][currency] = numericToAmount
        }
      }

      const selectionValue = isCustomAmountSelection
        ? CUSTOM_AMOUNT_SELECTION_VALUE
        : hasKnownAmount
        ? formatAmountSelectionValue(numericToAmount, currency)
        : null

      const selectionKey = isCustomAmountSelection
        ? CUSTOM_AMOUNT_SELECTION_VALUE
        : hasKnownAmount
        ? `${currency}:${Math.round(numericToAmount)}`
        : null

      if (!selectionValue || !selectionKey || shouldSuppressTracking) {
        return
      }

      if (selectionKey === lastTrackedAmountSelectionKey) {
        return
      }

      lastTrackedAmountSelectionKey = selectionKey

      element.hostPageEventInstrumentation?.track(studioGridAmountSelectionEvent, {
        campaignId: element.campaignId,
        data: {
          ...(!isCustomAmountSelection && hasKnownAmount
            ? { donation_amount: numericToAmount }
            : {}),
          from_amount,
          to_amount,
          value: selectionValue,
        },
      })
    })

    // Subscribe to currency selection changes
    donationForm.addEventListener(
      'currency_selected',
      async (event: CustomEvent<CurrencySelectedDetail>) => {
        const shouldSuppressTracking = isHostPageEventSyncSuppressed()
        if (!element.config) {
          return
        }

        const { from_currency, to_currency } = event.detail
        const targetCurrency = to_currency
        const previousCurrency = from_currency || currentCurrency || baseCurrency || 'USD'

        if (!targetCurrency || previousCurrency === targetCurrency) {
          return
        }

        // Update currentCurrency BEFORE setting the config to ensure that any
        // amount_selected events dispatched by the form during config update
        // use the correct currency for tracking.
        currentCurrency = targetCurrency

        await updatePresetAmountsForCurrency(targetCurrency)

        // @ts-expect-error | Custom element custom attribute
        donationForm.config = element.config?.props || {}

        if (!shouldSuppressTracking) {
          element.hostPageEventInstrumentation?.track(studioGridCurrencySelectionEvent, {
            campaignId: element.campaignId,
            data: {
              from_currency,
              to_currency: targetCurrency,
            },
          })
        }

        // If there are frequencies where the selected preset is the custom "Other"
        // option, convert their stored custom values from the previous currency into
        // the new currency and push all of them into the web component via the
        // externalCustomAmounts API.
        try {
          await updateCustomOtherAmountsForCurrency(previousCurrency, targetCurrency)
        } catch {
          // If conversion for the custom amounts fails, we still keep the rest
          // of the currency change behavior working.
        }
      }
    )
  } catch (error) {
    console.error('Error replacing element with donation form:', error)
  }
}

const handleClassyHref: StrategyHandler = (element) => {
  element.el.addEventListener('click', (event: MouseEvent) => {
    event.preventDefault()

    const href = element.el.getAttribute('href')

    if (href) {
      const params = extractClassyParamsFromHref(href)
      element.modalSDK.open(element.campaignId, params)
    }
  })
}

const handleDefault: StrategyHandler = () => {
  console.log('Default strategy: no specific action taken.')
}

export const strategyHandlers: Record<CrawlerElementStrategy, StrategyHandler> = {
  donationForm: handleDonationForm,
  classyHref: handleClassyHref,
  default: handleDefault,
}
